import type {
  AccountAdmin,
  AccountAdminWriteInput,
  ArchivedAdmin,
  ArchivedBranch,
  ArchivedBranchMatch,
  ArchivedCompany,
  ArchivedDepartment,
  ArchivedDepartmentMatch,
  ArchivedRegion,
  ArchivedRegionMatch,
  Branch,
  BranchUpdateInput,
  BranchWriteInput,
  Company,
  CompanySummary,
  CompanyWriteInput,
  Country,
  Currency,
  DepartmentDetails,
  DepartmentUpdateInput,
  DepartmentWriteInput,
  Governorate,
  Region,
  RegionPolygon,
  RegionUpdateInput,
  RegionWriteInput,
} from "../domain";
import type {
  AccountAdminCreateWriteDto,
  AccountAdminDto,
  AccountAdminUpdateWriteDto,
  ArchivedAdminListItemDto,
  ArchivedBranchListItemDto,
  ArchivedBranchMatchDto,
  ArchivedCompanyListItemDto,
  ArchivedDepartmentDto,
  ArchivedDepartmentMatchDto,
  ArchivedRegionDto,
  ArchivedRegionMatchDto,
  BranchCreateWriteDto,
  BranchDto,
  BranchUpdateWriteDto,
  CompanyCreateWriteDto,
  CompanyDto,
  CompanySummaryDto,
  CompanyUpdateWriteDto,
  CountryDto,
  CurrencyDto,
  DepartmentCreateWriteDto,
  DepartmentDto,
  DepartmentUpdateWriteDto,
  GovernorateDto,
  RegionCreateWriteDto,
  RegionDto,
  RegionUpdateWriteDto,
} from "./organizationDtos";

/** Serves both the list and the details: `DepartmentDetails` extends the
 *  list's `Department`, so a list row just carries a few unused fields. */
export function departmentFromDto(dto: DepartmentDto): DepartmentDetails {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    branch:
      dto.branch === null || dto.branch === undefined
        ? null
        : {
            id: dto.branch.id,
            nameEn: dto.branch.nameEn,
            nameAr: dto.branch.nameAr,
          },
    isActive: dto.isActive,
    remarks: dto.remarks,
  };
}

export function archivedDepartmentFromDto(
  dto: ArchivedDepartmentDto,
): ArchivedDepartment {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    archivedAt: dto.archivedAt,
  };
}

export function archivedDepartmentMatchFromDto(
  dto: ArchivedDepartmentMatchDto,
): ArchivedDepartmentMatch {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    deletedAt: dto.deletedAt,
  };
}

export function departmentCreateToDto(
  input: DepartmentWriteInput,
): DepartmentCreateWriteDto {
  return {
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    ...(input.remarks === undefined ? {} : { remarks: input.remarks }),
  };
}

/** Keeps only the keys that are set -- `remarks: null` survives (it clears). */
export function departmentUpdateToDto(
  input: DepartmentUpdateInput,
): DepartmentUpdateWriteDto {
  return {
    ...(input.nameEn === undefined ? {} : { nameEn: input.nameEn }),
    ...(input.nameAr === undefined ? {} : { nameAr: input.nameAr }),
    ...(input.isActive === undefined ? {} : { isActive: input.isActive }),
    ...(input.remarks === undefined ? {} : { remarks: input.remarks }),
  };
}

export function branchFromDto(dto: BranchDto): Branch {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    isMain: dto.isMain,
    isActive: dto.isActive,
    countryId: dto.countryId ?? null,
    governorateId: dto.governorateId ?? null,
    country: dto.country ?? null,
    governorate: dto.governorate ?? null,
    location:
      dto.latitude === null ||
      dto.latitude === undefined ||
      dto.longitude === null ||
      dto.longitude === undefined
        ? null
        : { latitude: dto.latitude, longitude: dto.longitude },
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    accountId: dto.accountId,
    createdById: dto.createdById ?? null,
    updatedById: dto.updatedById ?? null,
  };
}

export function branchCreateToDto(
  input: BranchWriteInput,
): BranchCreateWriteDto {
  return {
    ...(input.accountId === undefined ? {} : { accountId: input.accountId }),
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    ...(input.countryId === undefined ? {} : { countryId: input.countryId }),
    ...(input.governorateId === undefined
      ? {}
      : { governorateId: input.governorateId }),
    ...(input.location === undefined
      ? {}
      : {
          latitude: input.location.latitude,
          longitude: input.location.longitude,
        }),
  };
}

export function branchUpdateToDto(
  input: BranchUpdateInput,
): BranchUpdateWriteDto {
  return {
    ...(input.nameEn === undefined ? {} : { nameEn: input.nameEn }),
    ...(input.nameAr === undefined ? {} : { nameAr: input.nameAr }),
    ...(input.countryId === undefined ? {} : { countryId: input.countryId }),
    ...(input.governorateId === undefined
      ? {}
      : { governorateId: input.governorateId }),
    ...(input.location === undefined
      ? {}
      : {
          latitude: input.location.latitude,
          longitude: input.location.longitude,
        }),
    ...(input.isActive === undefined ? {} : { isActive: input.isActive }),
  };
}

export function archivedBranchFromDto(
  dto: ArchivedBranchListItemDto,
): ArchivedBranch {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    isActive: dto.isActive,
    account: { ...dto.account },
    archivedAt: dto.archivedAt,
  };
}

export function archivedBranchMatchFromDto(
  dto: ArchivedBranchMatchDto,
): ArchivedBranchMatch {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    accountId: dto.accountId,
    deletedAt: dto.deletedAt,
  };
}

// The wire boundary is an open ring of flat {latitude, longitude} points;
// the domain's RegionPolygon is a closed GeoJSON-style [lng, lat] ring (the
// map/geometry-validation code already speaks that shape). Both directions
// live here so the rest of the app never has to think about the wire format.
function regionPolygonFromBoundary(
  boundary: readonly { latitude: number; longitude: number }[],
): RegionPolygon {
  const ring = boundary.map(
    (point) => [point.longitude, point.latitude] as readonly [number, number],
  );
  const first = ring[0];
  return {
    type: "Polygon",
    coordinates: [first === undefined ? ring : [...ring, first]],
  };
}

function boundaryFromRegionPolygon(
  geometry: RegionPolygon,
): { latitude: number; longitude: number }[] {
  const ring = geometry.coordinates[0] ?? [];
  // Drop the closing point the domain ring repeats -- the API wants it open.
  const open = ring.slice(0, ring.length > 1 ? -1 : undefined);
  return open.map(([lng, lat]) => ({ latitude: lat, longitude: lng }));
}

export function regionFromDto(dto: RegionDto): Region {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    geometry: regionPolygonFromBoundary(dto.boundary),
    isActive: dto.status === "ACTIVE",
    accountId: dto.accountId,
    ...(dto.account === undefined ? {} : { account: dto.account }),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    createdById: dto.createdById ?? null,
    updatedById: dto.updatedById ?? null,
  };
}

export function regionCreateToDto(
  input: RegionWriteInput,
): RegionCreateWriteDto {
  return {
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    boundary: boundaryFromRegionPolygon(input.geometry),
  };
}

export function regionUpdateToDto(
  input: RegionUpdateInput,
): RegionUpdateWriteDto {
  return {
    ...(input.nameEn === undefined ? {} : { nameEn: input.nameEn }),
    ...(input.nameAr === undefined ? {} : { nameAr: input.nameAr }),
    ...(input.geometry === undefined
      ? {}
      : { boundary: boundaryFromRegionPolygon(input.geometry) }),
    ...(input.isActive === undefined
      ? {}
      : { status: input.isActive ? "ACTIVE" : "INACTIVE" }),
  };
}

export function archivedRegionFromDto(dto: ArchivedRegionDto): ArchivedRegion {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    isActive: dto.status === "ACTIVE",
    account: dto.account ?? { id: dto.accountId, nameEn: "", nameAr: "" },
    archivedAt: dto.archivedAt,
  };
}

export function archivedRegionMatchFromDto(
  dto: ArchivedRegionMatchDto,
): ArchivedRegionMatch {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    accountId: dto.accountId,
    deletedAt: dto.deletedAt,
  };
}

export function countryFromDto(dto: CountryDto): Country {
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
  };
}

export function governorateFromDto(dto: GovernorateDto): Governorate {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    countryId: dto.countryId,
  };
}

export function currencyFromDto(dto?: CurrencyDto): Currency {
  if (!dto) {
    return {
      id: "",
      code: "SAR",
      nameEn: "Saudi Riyal",
      nameAr: "ريال سعودي",
      symbol: "﷼",
    };
  }
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    symbol: dto.symbol,
  };
}

export function companyFromDto(dto: CompanyDto): Company {
  return {
    ...dto,
    // Absent on the create/update response (never on a fresher fetch than
    // this one) — a brand-new account is active with only the free CORE
    // grant, and every mutation navigates to a `getById` refetch anyway.
    isActive: dto.isActive ?? true,
    isFullySubscribed: dto.isFullySubscribed ?? true,
    currency: currencyFromDto(dto.currency),
    // `[]` here means "not reported this response", not "actually empty" —
    // see the schema comment. Callers needing the real list re-fetch.
    attachments: (dto.attachments ?? []).map((attachment) => ({
      ...attachment,
    })),
    admin:
      dto.admin === null || dto.admin === undefined
        ? null
        : {
            ...dto.admin,
            isAccountAdmin: dto.admin.isAccountAdmin ?? true,
          },
  };
}

export function companyCreateToDto(
  input: CompanyWriteInput,
): CompanyCreateWriteDto {
  return {
    ...(input.type === undefined ? {} : { type: input.type }),
    ...(input.address === undefined ? {} : { address: input.address }),
    ...(input.remarks === undefined ? {} : { remarks: input.remarks }),
    ...(input.phoneNumber2 === undefined
      ? {}
      : { phoneNumber2: input.phoneNumber2 }),
    ...(input.fax === undefined ? {} : { fax: input.fax }),
    ...(input.currencyId === undefined ? {} : { currencyId: input.currencyId }),
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    email: input.email,
    phoneNumber1: input.phoneNumber1,
  };
}

export function companyUpdateToDto(
  input: CompanyWriteInput,
): CompanyUpdateWriteDto {
  return {
    ...companyCreateToDto(input),
    ...(input.attachmentsIdsToBeDeleted === undefined
      ? {}
      : {
          attachmentsIdsToBeDeleted: [...input.attachmentsIdsToBeDeleted],
        }),
  };
}

export function companySummaryFromDto(dto: CompanySummaryDto): CompanySummary {
  return {
    accounts: dto.totalAccounts,
    active: dto.totalActiveAccounts,
    inactive: dto.totalInactiveAccounts,
    admins: dto.totalAccountsAdmins,
  };
}

export function archivedCompanyFromDto(
  dto: ArchivedCompanyListItemDto,
): ArchivedCompany {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    email: dto.email,
    admin: dto.admin ? { ...dto.admin } : null,
    archivedAt: dto.archivedAt,
  };
}

/** `isActive` is derived from `status`, not sent directly — the API has no top-level `isActive` field. */
export function accountAdminFromDto(dto: AccountAdminDto): AccountAdmin {
  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    isActive: dto.status === "ACTIVE",
    phoneNumber: dto.phoneNumber,
    enable2FA: dto.enable2FA,
    status: dto.status,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    accountId: dto.accountId,
    isAccountAdmin: dto.type ? dto.type.slug === "CLIENT_ADMIN" : true,
    account: dto.account
      ? { ...dto.account }
      : { id: dto.accountId, nameEn: "", nameAr: "" },
  };
}

export function archivedAdminFromDto(
  dto: ArchivedAdminListItemDto,
): ArchivedAdmin {
  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    account: dto.account ? { ...dto.account } : null,
    archivedAt: dto.archivedAt,
  };
}

export function accountAdminCreateToDto(
  input: AccountAdminWriteInput,
): AccountAdminCreateWriteDto {
  return {
    accountId: input.accountId,
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    username: input.username,
    email: input.email,
    phoneNumber: input.phoneNumber,
  };
}

/** No `accountId`/`username` — the API accepts neither on update. */
export function accountAdminUpdateToDto(
  input: AccountAdminWriteInput,
): AccountAdminUpdateWriteDto {
  return {
    nameEn: input.nameEn,
    nameAr: input.nameAr,
    email: input.email,
    phoneNumber: input.phoneNumber,
    ...(input.status === undefined ? {} : { status: input.status }),
  };
}
